import React, { useEffect, useRef, useState } from 'react'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { FiChevronDown, FiGlobe, FiTarget, FiZap, FiPlay } from 'react-icons/fi'

const Hero = () => {
  const { scrollY } = useScroll()
  const y1 = useTransform(scrollY, [0, 500], [0, 200])
  const opacity = useTransform(scrollY, [0, 300], [1, 0])
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])
  
  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 80
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = element.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  return (
    <section id="home" className="relative min-h-screen w-full overflow-hidden bg-gradient-to-b from-white via-rose-50/30 to-gray-50 flex items-center justify-center pt-16 sm:pt-20">
      {/* Animated Gradient Orbs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div 
          style={{ y: y1 }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-rose-50/50 to-white z-10" />
          <img 
            src="https://images.unsplash.com/photo-1590480370914-1e582855f756?auto=format&fit=crop&q=80" 
            alt="Udaipur Heritage" 
            className="w-full h-full object-cover opacity-40 grayscale-[30%] transition-transform duration-1000 scale-110"
          />
        </motion.div>
        
        {/* Floating Color Orbs */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-20 left-[15%] w-64 h-64 bg-gradient-to-r from-accent-maroon/20 to-accent-maroonDark/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0],
            x: [0, -40, 0],
            y: [0, 40, 0]
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-20 right-[15%] w-80 h-80 bg-gradient-to-r from-rose-300/20 to-red-300/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1, 1.3, 1],
            rotate: [0, -180, -360],
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-pink-200/15 to-red-200/15 rounded-full blur-3xl"
        />
      </div>

      {/* Interactive Particle Field */}
      <div className="absolute inset-0 z-5 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-accent-maroon/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      {/* Floating Glassmorphism Cards (Desktop Only) */}
      <div className="absolute inset-0 z-10 pointer-events-none hidden lg:block">
        <motion.div 
          animate={{ 
            y: [0, -30, 0],
            rotate: [0, 5, 0],
            scale: [1, 1.05, 1]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-[10%] p-6 bg-white/60 backdrop-blur-xl border border-accent-maroon/30 rounded-3xl shadow-2xl"
        >
          <div className="flex items-center space-x-4 mb-3">
            <motion.div 
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 rounded-xl bg-gradient-to-r from-accent-maroon to-accent-maroonDark flex items-center justify-center text-white"
            >
              <FiTarget size={24} />
            </motion.div>
            <div>
              <p className="text-gray-800 font-black text-xs">LIVE TARGET</p>
              <p className="text-gray-600 text-[10px] font-bold">LAKE CLEANUP 2024</p>
            </div>
          </div>
          <div className="w-48 h-2 bg-gray-200 rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: '75%' }}
              transition={{ duration: 2, delay: 1.5 }}
              className="h-full bg-gradient-to-r from-accent-maroon to-accent-maroonDark"
            />
          </div>
        </motion.div>

        <motion.div 
          animate={{ 
            y: [0, 30, 0],
            rotate: [0, -5, 0],
            scale: [1, 1.05, 1]
          }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-1/4 left-[10%] p-6 bg-white/60 backdrop-blur-xl border border-accent-maroon/30 rounded-3xl shadow-2xl"
        >
          <div className="flex items-center space-x-4">
            <motion.div 
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-12 h-12 rounded-xl bg-gradient-to-r from-accent-maroon to-accent-maroonDark flex items-center justify-center text-white"
            >
              <FiZap size={24} />
            </motion.div>
            <div>
              <p className="text-gray-800 font-black text-xs">5,000+ HEROES</p>
              <p className="text-gray-600 text-[10px] font-bold">JOINED THE MISSION</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Main Content Area */}
      <motion.div 
        style={{ opacity }}
        className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 text-center"
      >
      {/* Animated Badge with Glow */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5, y: -50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ 
          type: "spring",
          stiffness: 300,
          damping: 20,
          delay: 0.2
        }}
        style={{ marginTop: 34 }}
        className="inline-flex items-center gap-3 px-6 py-3 rounded-full border-2 border-accent-maroon/50 bg-white/80 backdrop-blur-md mb-12 shadow-xl"
      >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 rounded-full bg-gradient-to-r from-accent-maroon to-accent-maroonDark flex items-center justify-center"
          >
            <FiGlobe className="text-white" size={16} />
          </motion.div>
          <span className="text-accent-maroon text-xs font-black uppercase tracking-[0.2em]">
            Swachh Mewar Samriddhi
          </span>
        </motion.div>

        {/* Massive Headline with Indian Flag Colors */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <h1 className="text-5xl sm:text-7xl md:text-9xl lg:text-[10rem] font-black font-grotesk tracking-tighter leading-[0.8] mb-8">
            <motion.span
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="block text-orange-500"
              style={{ filter: 'drop-shadow(0 0 10px rgba(249, 115, 22, 0.3))' }}
            >
              SWACHH MEWAR
            </motion.span>
            <motion.span 
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="block bg-gradient-to-r from-orange-500 via-white via-yellow-300 to-green-600 bg-clip-text text-transparent pb-4 relative"
              style={{
                backgroundSize: '300% 300%',
                filter: 'drop-shadow(0 0 15px rgba(34, 197, 94, 0.3))',
              }}
            >
              SAMRIDDHI.
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-md"
                animate={{ 
                  x: ['-200%', '200%'],
                  opacity: [0, 0.8, 0]
                }}
                transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 0.8 }}
              />
            </motion.span>
          </h1>
        </motion.div>

        {/* Sub-headline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="text-gray-600 text-xl md:text-2xl font-medium max-w-3xl mx-auto mb-16 leading-relaxed"
        >
          We are NOT just a website, we are out on the ground! Join thousands of local brothers and sisters in cleaning our beautiful lakes and restoring Udaipur's true pride. Fill out the volunteer form below and clean our city!
        </motion.p>

        {/* Action Controls */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 px-4"
        >
          {/* Main CTA - Missions */}
          <motion.button 
            whileHover={{ 
              scale: 1.05, 
              boxShadow: "0 0 40px rgba(128,0,0,0.4)",
              y: -3
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('book')}
            className="group relative px-8 sm:px-12 py-4 sm:py-6 bg-gradient-to-r from-accent-maroon to-accent-maroonDark text-white font-black uppercase tracking-[0.15em] sm:tracking-[0.25em] text-xs sm:text-sm rounded-full overflow-hidden shadow-2xl border-2 border-white/30 w-full sm:w-auto"
          >
            <span className="relative z-10 flex items-center font-bold">
              Choose Cleaning Slot
              <FiArrowRight className="ml-2 sm:ml-3 transition-transform group-hover:translate-x-1" />
            </span>
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.4 }}
            />
          </motion.button>

          {/* Main CTA - Volunteer Form */}
          <motion.button 
            whileHover={{ 
              scale: 1.05, 
              boxShadow: "0 0 40px rgba(160,0,0,0.4)",
              y: -3
            }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('volunteers')}
            className="group relative px-8 sm:px-12 py-4 sm:py-6 bg-gradient-to-r from-accent-maroon to-accent-maroonDark text-white font-black uppercase tracking-[0.15em] sm:tracking-[0.25em] text-xs sm:text-sm rounded-full overflow-hidden shadow-2xl border-2 border-white/30 w-full sm:w-auto"
          >
            <span className="relative z-10 flex items-center font-bold">
              Become a Volunteer Now!
              <FiZap className="ml-2 sm:ml-3 text-white animate-pulse" />
            </span>
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.4 }}
            />
          </motion.button>
        </motion.div>

        {/* Secondary Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="mt-12 sm:mt-16 flex items-center justify-center gap-8 sm:gap-12"
        >
          <motion.button 
            whileHover={{ scale: 1.1 }}
            onClick={() => scrollToSection('missions')}
            className="group flex items-center space-x-3 sm:space-x-4 text-gray-800 font-black text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em]"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-accent-maroon/50 flex items-center justify-center group-hover:bg-accent-maroon/10 group-hover:border-accent-maroon transition-all shadow-lg">
              <FiPlay className="fill-gray-800 ml-0.5 sm:ml-1" size={14} />
            </div>
            <span>Watch Film</span>
          </motion.button>
          
          <motion.button 
            whileHover={{ scale: 1.1 }}
            onClick={() => scrollToSection('heritage')}
            className="text-gray-500 hover:text-accent-maroon font-black text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] transition-colors"
          >
            Explore Map
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator with Glow */}
      <motion.div 
        animate={{ y: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 hidden md:flex flex-col items-center gap-4"
      >
        <motion.span 
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-[10px] font-black text-accent-maroon uppercase tracking-[0.5em] rotate-90 mb-8 origin-left"
        >
          Scroll
        </motion.span>
        <motion.div 
          animate={{ height: [40, 60, 40] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-[2px] bg-gradient-to-b from-accent-maroon via-accent-maroonDark to-transparent rounded-full"
        />
      </motion.div>
    </section>
  )
}

const FiArrowRight = ({ className }) => (
  <svg 
    stroke="currentColor" fill="none" strokeWidth="3" viewBox="0 0 24 24" 
    strokeLinecap="round" strokeLinejoin="round" height="1em" width="1em" 
    xmlns="http://www.w3.org/2000/svg" className={className}
  >
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
)

export default Hero
